import BaseService from '@/lib/BaseService'
import type { AxiosResponse } from 'axios'

/**
 * Short-window dedupe for SDUIGrid's reads, with refcounted wire cancellation.
 *
 * Identical requests (same method + url + body) issued within TTL_MS share a
 * single network call — whether they overlap (in-flight) or the second fires
 * just after the first settled (the cached promise is reused). The key includes
 * the body, so a genuinely different request — a param-ful refetch once an
 * ancestor row arrives, or a different page/sort/filter — is never deduped.
 * After a mutation, callers invalidate the cache (see {@link invalidateGridCache})
 * so a refresh hits the network. The window is far shorter than any
 * user-initiated refetch, so it never serves genuinely stale data.
 *
 * Failures are cached too (negative caching). A failing endpoint (e.g. a 403 on
 * a license grid that lacks scope, or a flaky 5xx) would otherwise be re-fetched
 * from the network on EVERY remount/refetch, because a rejected entry left no
 * trace — turning any repeated trigger (tab re-activation, dev StrictMode, a
 * render-driven refetch) into an unthrottled flood of identical failing calls.
 * Keeping the rejected promise for TTL_MS collapses that flood into at most one
 * network attempt per window: callers still receive the rejection (and surface
 * the error), but the request itself isn't re-issued until the window elapses.
 * Genuine retry paths are unaffected — a refetch with new params has a different
 * key, and a post-mutation refresh calls {@link invalidateGridCache}.
 *
 * Cancellation: callers may pass an AbortSignal, registering themselves as one
 * CONSUMER of the shared request. The underlying network request is aborted
 * only when EVERY signal-bearing consumer has aborted (signal-less consumers
 * count and never release, so the request can't be killed under them). This is
 * what frees an HTTP/1.1 connection when a grid unmounts (tab switch) or its
 * fetch is superseded (ancestor row arriving re-scopes the body) — previously
 * the superseded request ran to completion on the wire, and rapid tab
 * switching stacked enough of them to queue every other request for hundreds
 * of ms on hosted environments. A wire-aborted entry is evicted immediately
 * and its rejection is NEVER negative-cached (an abort is a cancellation, not
 * a server failure), so a quick revisit refetches instead of awaiting a doomed
 * promise. Dev-mode StrictMode double-mount aborts the first request
 * synchronously — before axios's microtask dispatches the XHR — so the wire
 * still sees at most one request; there is deliberately no grace timer, which
 * keeps this module timer-free and deterministic under fake timers in tests.
 */

interface Entry {
    promise: Promise<AxiosResponse>
    /**
     * Epoch ms after which a SETTLED entry is stale and must be re-fetched.
     * `Infinity` while the request is in flight, so a request slower than TTL_MS
     * is never evicted mid-flight (which would let a concurrent identical request
     * start a second network call). Stamped to `now + TTL_MS` once it settles,
     * for both success and failure.
     */
    expiresAt: number
    /** Controls the underlying axios request; null once settled. */
    controller: AbortController | null
    /**
     * Consumers registered on this entry. Signal-less consumers increment but
     * never decrement, so the entry is never wire-aborted under them.
     */
    activeConsumers: number
    settled: boolean
}

const TTL_MS = 1500
const cache = new Map<string, Entry>()

const keyOf = (method: string, url: string, body?: unknown): string =>
    `${method} ${url} ${body === undefined ? '' : JSON.stringify(body)}`

const dedupe = (
    key: string,
    exec: (signal: AbortSignal) => Promise<AxiosResponse>,
    consumerSignal?: AbortSignal,
): Promise<AxiosResponse> => {
    const nowMs = Date.now()
    // Sweep settled-and-expired entries so the map can't grow unbounded across a
    // session. In-flight entries (expiresAt === Infinity) are never swept.
    for (const [k, entry] of cache) {
        if (nowMs >= entry.expiresAt) cache.delete(k)
    }

    // An already-aborted consumer must neither start a request nor prolong an
    // existing one.
    if (consumerSignal?.aborted) {
        return Promise.reject(
            consumerSignal.reason ?? new DOMException('Aborted', 'AbortError'),
        )
    }

    let entry = cache.get(key)
    if (!entry) {
        const controller = new AbortController()
        const created: Entry = {
            promise: exec(controller.signal),
            expiresAt: Number.POSITIVE_INFINITY,
            controller,
            activeConsumers: 0,
            settled: false,
        }
        entry = created
        cache.set(key, created)

        // Stamp the expiry from the SETTLE time (not the start) so the dedupe
        // window is measured from when the result is known, for both outcomes.
        // Caching the failure here is the flood guard — see the file doc.
        // Attaching the handler also marks `promise` as handled, so a reused
        // rejection never surfaces as an unhandled-rejection warning (every
        // real caller awaits it and handles it).
        const settle = () => {
            created.settled = true
            created.controller = null
            // Identity guard: invalidateGridCache may have replaced this entry
            // meanwhile — never touch the replacement.
            if (cache.get(key) !== created) return
            if (controller.signal.aborted) {
                // Our own all-consumers-gone abort: a cancellation, not a
                // server outcome — evict (belt and braces with the eager
                // delete in the abort listener) so it is never negative-cached.
                cache.delete(key)
            } else {
                created.expiresAt = Date.now() + TTL_MS
            }
        }
        created.promise.then(settle, settle)
    }

    entry.activeConsumers += 1
    if (consumerSignal) {
        const consumed = entry
        consumerSignal.addEventListener(
            'abort',
            () => {
                consumed.activeConsumers -= 1
                if (consumed.activeConsumers > 0 || consumed.settled) return
                // Last live consumer gone while still in flight: cancel on the
                // wire (frees the HTTP/1.1 connection) and evict eagerly so a
                // quick revisit starts a fresh request instead of awaiting a
                // doomed promise.
                consumed.controller?.abort()
                if (cache.get(key) === consumed) cache.delete(key)
            },
            { once: true },
        )
    }

    return entry.promise
}

export interface DedupeOptions {
    /**
     * Registers the caller as one consumer of the shared request. The wire
     * request is aborted only when EVERY signal-bearing consumer has aborted.
     */
    signal?: AbortSignal
}

export const postGridDeduped = (
    endpoint: string,
    body: unknown,
    options?: DedupeOptions,
): Promise<AxiosResponse> =>
    dedupe(
        keyOf('POST', endpoint, body),
        (signal) => BaseService.post(endpoint, body, { signal }),
        options?.signal,
    )

export const getGridDeduped = (
    url: string,
    options?: DedupeOptions,
): Promise<AxiosResponse> =>
    dedupe(
        keyOf('GET', url),
        (signal) => BaseService.get(url, { signal }),
        options?.signal,
    )

/** Clear all cached grid reads — call after a mutation so a refresh hits the network. */
export const invalidateGridCache = (): void => {
    cache.clear()
}
